import * as THREE from 'three';

import { CORNERS, elevationToY } from '../map/HexMetrics';
import { Axial, axialKey, axialToWorld, neighbor } from '../map/HexCoordinates';
import { HexCellData, cornerElevation, isWater, waterLevel } from '../map/HexSurface';
import { habitable, passable } from '../map/Movement';

/**
 * Po czym nie da się chodzić — na mapie, w trybie debug.
 *
 * **Dwa różne „nie" i dlatego dwa kolory.** Czerwone pole odmawia samo z siebie:
 * jest wodą albo gąszczem, i nie wejdzie się na nie znikąd. Pomarańczowe jest
 * do stania, ale **odcięte** — każdy z sześciu sąsiadów leży za wysoko albo za
 * nisko, więc nie ma jak do niego wejść ani z niego wyjść. Pierwsze jest faktem
 * o polu, drugie o **regule między polami**, i tylko drugie zmienia się, gdy
 * ktoś pokręci progiem kroku.
 *
 * Czytane bliźniakiem reguł ruchu, nie ich kopią: to `habitable` i `passable`
 * z `Movement.ts` odpowiadają na oba pytania, więc nakładka pokazuje dokładnie
 * to, czego odmówi serwer — a nie własne wyobrażenie o tym.
 *
 * Budowane wyłącznie, gdy nakładka jest włączona: sześć trójkątów na pole razy
 * tysiąc pól na chunk to nie jest koszt, który ma leżeć na mapie przez całą grę
 * dla narzędzia oglądanego przez minutę.
 */

/**
 * Ile nad powierzchnią leży płatek.
 *
 * **Nad powierzchnią, a nie nad wysokością pola** — i to jest różnica, która
 * decyduje o tym, czy nakładkę w ogóle widać. Wierzchołek narożny terenu jest
 * średnią z trzech pól, więc na zboczu leży nawet o cały poziom wyżej niż
 * środek pola: płatek płaski, położony na wysokości środka, chował się w ziemi
 * dokładnie tam, gdzie było najciekawiej — na stromiźnie. Nad wodą było gorzej,
 * bo tam „wysokość pola" to **dno**, a nad nim stoi nieprzezroczyste lustro.
 */
const LIFT = 0.05;

/** Trochę mniejszy niż heks, żeby było widać, gdzie kończy się jedno pole. */
const SHRINK = 0.92;

/** Nie do stania: woda, gąszcz, głazowisko. */
const GROUND_REFUSES = new THREE.Color(0xd05050);

/** Do stania, ale nie ma jak wejść: wszyscy sąsiedzi za stromo. */
const CUT_OFF = new THREE.Color(0xe0a040);

export function createBlockedMaterial(): THREE.MeshBasicMaterial {
  return new THREE.MeshBasicMaterial({
    vertexColors: true,
    transparent: true,
    opacity: 0.45,
    depthWrite: false,
    side: THREE.FrontSide,
    fog: false,
  });
}

/**
 * Płatki nad polami, po których nie da się chodzić. `null`, gdy chunk jest cały
 * przechodni.
 */
export function buildBlockedGeometry(
  members: HexCellData[],
  world: Map<number, HexCellData>
): THREE.BufferGeometry | null {
  const positions: number[] = [];
  const colors: number[] = [];
  const indices: number[] = [];

  for (const cell of members) {
    const tint = verdict(cell, world);
    if (!tint) continue;

    const { x, z } = axialToWorld(cell.q, cell.r);

    // Woda siada na swoim lustrze, ląd na własnej powierzchni. Pole wodne
    // niesie wysokość **dna**, a płatek pod dnem jest płatkiem, którego nie ma.
    const mirror = waterLevel(cell);
    const flat = isWater(cell) && mirror !== null;

    const middle = positions.length / 3;
    positions.push(x, elevationToY(flat ? mirror : cell.elevation) + LIFT, z);
    colors.push(tint.r, tint.g, tint.b);

    for (let corner = 0; corner < 6; corner += 1) {
      // Narożnik idzie za terenem — tą samą średnią z trzech pól, którą liczy
      // mesh — więc płatek leży **na** zboczu zamiast przecinać je w poprzek.
      const height = flat ? mirror : cornerElevation(world, cell, corner);
      positions.push(
        x + CORNERS[corner].x * SHRINK,
        elevationToY(height) + LIFT,
        z + CORNERS[corner].z * SHRINK
      );
      colors.push(tint.r, tint.g, tint.b);
    }

    for (let corner = 0; corner < 6; corner += 1) {
      // Zgodnie z tym, jak nawinięty jest wachlarz terenu — inaczej płatek
      // byłby odwrócony tyłem i niewidoczny.
      indices.push(middle, middle + 1 + corner, middle + 1 + ((corner + 1) % 6));
    }
  }

  if (indices.length === 0) return null;

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  geometry.setIndex(indices);
  geometry.computeBoundingSphere();
  return geometry;
}

/**
 * Dlaczego to pole odmawia — albo `null`, gdy nie odmawia wcale.
 *
 * Czerwone jest **każde** pole, po którym nie da się chodzić: morze, jezioro,
 * gąszcz i głazowisko powyżej progu przejścia. Pomarańczowe — tylko te, które
 * są do stania, a mimo to nie mają wejścia.
 *
 * Pole, którego sąsiedztwo nie zostało jeszcze wczytane, **nie jest sądzone**
 * jako odcięte: „odcięty" liczy się z sześciu sąsiadów, a brak jednego z nich
 * to nie jest odpowiedź „nie da się". Grunt odmawiający sam z siebie nie potrzebuje
 * do tego żadnego sąsiada i jest malowany zawsze.
 */
function verdict(cell: HexCellData, world: Map<number, HexCellData>): THREE.Color | null {
  if (!habitable(cell)) return GROUND_REFUSES;

  let known = 0;
  for (let direction = 0; direction < 6; direction += 1) {
    const side: Axial = neighbor(cell, direction);
    const there = world.get(axialKey(side.q, side.r));
    if (!there) continue;

    known += 1;
    // Wejść **i** wyjść: reguła wysokości jest symetryczna, więc wystarczy
    // jedna strona, ale sąsiad musi być do stania sam z siebie.
    if (passable(cell, there)) return null;
  }

  return known === 6 ? CUT_OFF : null;
}
